import { api } from "@/lib/api";

// Offline-safe queue for SAFE POS/KDS actions ONLY (order status transitions).
// Online payments are NEVER queued here and are never considered successful offline.
// The queue is idempotent (duplicate id+status transitions are collapsed) and
// resynchronises automatically when connectivity returns.

const KEY = "novanext_offline_queue_v1";
const listeners = new Set();

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const write = (q) => {
  localStorage.setItem(KEY, JSON.stringify(q));
  listeners.forEach((l) => l(q.length));
};

export const queueCount = () => read().length;
export const onQueueChange = (cb) => {
  listeners.add(cb);
  cb(read().length);
  return () => listeners.delete(cb);
};

// axios network errors have no `response`; treat those (or explicit offline) as "retry later".
const isNetworkError = (e) => !e || !e.response;
const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

function enqueue(id, status) {
  const q = read();
  // Idempotency + double-click guard: never queue the same pending id+status twice.
  if (q.some((x) => x.id === id && x.status === status)) return;
  q.push({ key: `${id}:${status}:${Date.now()}`, id, status, at: new Date().toISOString() });
  write(q);
}

// Returns { order } when applied online, or { queued: true } when stored for later.
export async function submitOrderStatus(id, status) {
  if (isOffline()) { enqueue(id, status); return { queued: true }; }
  try {
    const order = await api.updateOrderStatus(id, status);
    return { order, queued: false };
  } catch (e) {
    if (isNetworkError(e)) { enqueue(id, status); return { queued: true }; }
    throw e; // real server error (e.g. invalid transition) — surface to caller
  }
}

let flushing = false;
export async function flushQueue(onApplied) {
  if (flushing || isOffline()) return;
  flushing = true;
  try {
    let q = read();
    while (q.length) {
      const item = q[0];
      try {
        const order = await api.updateOrderStatus(item.id, item.status);
        onApplied?.(order);
        q = read().filter((x) => x.key !== item.key);
        write(q);
      } catch (e) {
        if (isNetworkError(e)) break;              // still offline → keep for later
        q = read().filter((x) => x.key !== item.key); // poison (server rejected) → drop
        write(q);
      }
    }
  } finally {
    flushing = false;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => flushQueue());
}
